import React from 'react';
import Head from 'next/head';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import { NextPageWithLayout } from './_app';
import Layout from '@/index-layouts';
import { AppSettings } from '@/shared/app-common';
import { getTdkApi } from '@/lib/service/tdk';
import { page2JsonLd, faqJsonLd1 } from '@/lib/common/jsonld';
import { ISR_REVALIDATE_TIME } from '@/lib/config/isr';
import { DynamicISRDebugPanel as ISRDebugPanel } from '@/components/ISRDebugPanel/dynamic';
import AffiliateProgramContent from '@/components/AffiliateProgram';
import { FAQ_ITEMS } from '@/components/AffiliateProgram/constants';

const PAGE_PATH = '/affiliate-program';
const LOCALES = ['en', 'zh', 'tw', 'ja', 'ko', 'pt', 'de', 'es', 'it'];
const OG_IMAGE = 'https://res-front.pumpsoul.com/prod-erasa/image/dmca-protect/dmca-5.png';

// ⭐ ISR：本页文案全部固定在 i18n，不依赖 CMS，因此不需要 getPageContentContentApi
export const getStaticProps: GetStaticProps = async (context) => {
  const { locale = 'en' } = context;

  const [localeRes, tdkData] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(PAGE_PATH, locale),
  ]);

  const host = process.env.NEXT_APP_WEB_HOST;
  const currentURL = locale === 'en' ? `${host}${PAGE_PATH}` : `${host}/${locale}${PAGE_PATH}`;
  const siteUrl = locale === 'en' ? `${host}` : `${host}/${locale}`;
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};

  const pageTitle = tdkData?.data?.title ?? tempData['head_AffiliateProgram_Title'] ?? null;
  const pageDescription = tdkData?.data?.description ?? tempData['head_AffiliateProgram_Desc'] ?? null;
  const pageKeywords = tdkData?.data?.keywords ?? tempData['head_AffiliateProgram_Keys'] ?? null;

  const jsonLd = page2JsonLd({ title: pageTitle, desc: pageDescription, url: currentURL }, { url: siteUrl });

  // FAQPage 结构化数据：问答与 FAQ 区块共用 FAQ_ITEMS，避免两处文案漂移。
  // q / a 本身就是 i18n 的 key，这里用同一份 tempData 取当前语言的译文，
  // 没有译文的语言回落成英文原句 —— 与页面上看到的完全一致。
  const faqJSONLd = faqJsonLd1(
    { title: pageTitle, desc: pageDescription, url: currentURL },
    { url: siteUrl },
    FAQ_ITEMS.map(({ q, a }) => ({ question: tempData[q] ?? q, answer: tempData[a] ?? a })),
  );

  return {
    props: {
      ...localeRes,
      herf: currentURL,
      locale,
      title: pageTitle,
      description: pageDescription,
      keywords: pageKeywords,
      jsonLd,
      faqJSONLd,
      // ⭐ ISR 信息
      isrGeneratedAt: new Date().toISOString(),
      isrRevalidateIn: ISR_REVALIDATE_TIME,
    },
    revalidate: ISR_REVALIDATE_TIME,
  };
};

const AffiliateProgramPage: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props: any) => {
  return (
    <>
      <ISRDebugPanel
        generatedAt={props.isrGeneratedAt}
        revalidateIn={props.isrRevalidateIn}
        pageName="Affiliate Program"
        additionalInfo={{
          语言: props.locale,
        }}
      />
      <AffiliateProgramContent />
    </>
  );
};

AffiliateProgramPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const description = page?.props?.description;
  const keywords = page?.props?.keywords;
  const jsonLd = page?.props?.jsonLd;
  const faqJSONLd = page?.props?.faqJSONLd;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={description} />
          <meta name="keywords" content={keywords} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={OG_IMAGE} />
          <meta property="og:image:secure_url" content={OG_IMAGE} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={OG_IMAGE} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=1, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
          />
          {LOCALES.map((lng) => (
            <link
              key={lng}
              rel="alternate"
              hrefLang={lng}
              href={`${process.env.NEXT_APP_WEB_HOST}/${lng}${PAGE_PATH}`}
            />
          ))}
          <link rel="alternate" hrefLang="x-default" href={`${process.env.NEXT_APP_WEB_HOST}${PAGE_PATH}`} />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {jsonLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
          )}
          {faqJSONLd && (
            <script async type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJSONLd) }} />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default AffiliateProgramPage;
